import { CaseAlertType } from '../enums';
import { EMPTY_NOTE } from './amplify/fixture/note';
import { segmentize } from './highlighter/highlighter-utils';
import { cloneDeep } from './object-utils';

export interface Dictionary<T> {
  [index: string]: T;
}

export const MimeTypes: { [name: string]: string } = {
  HTML: 'text/html',
  TEXT: 'text/plain',
  PDF: 'application/pdf',
  RTF: 'application/rtf'
};

const DOCUMET_CONVERSION_MAP = {
  // if there's an RTF than we expect the HTML
  // this can be extended to other mime types in the future.
  [MimeTypes.RTF]: [MimeTypes.HTML]
};

export const docComparitor = (
  doc1: CMxAPI.DocumentMetadata,
  doc2: CMxAPI.DocumentMetadata
) => {
  if (doc1.id === doc2.id) {
    return 0;
  }
  if (doc1.id > doc2.id) {
    return 1;
  }
  return -1;
};

const fetchLatestDoc = (docs: CMxAPI.DocumentMetadata[]) => {
  return [...docs].sort(docComparitor).pop();
};

/**
 * Gets the expected conversion target formats for documents in the given document groups.
 * Returns the first matching conversion target formats found, or an empty array if none found.
 * @param docGroups Dictionary of document metadata arrays grouped by some key
 * @returns Array of expected conversion target mime types
 */
export const getExpectedConversionTargets = (
  docGroups: Dictionary<CMxAPI.DocumentMetadata[]>
) => {
  for (const docs of Object.values(docGroups)) {
    for (const doc of docs) {
      const expectedFormats = DOCUMET_CONVERSION_MAP[doc.mimeType];
      if (expectedFormats) {
        return expectedFormats;
      }
    }
  }
  return [];
};

/**
 * A note may not be segmentized, but it still has content.  This function will build
 * segments from the content if not present.
 * @param testNote {CMxCommonApp.DecoratedNote}
 * @returns
 */
export const shimSegmentsIfNeeded = (testNote: CMxCommonApp.DecoratedNote) => {
  const note = cloneDeep(testNote);
  if (testNote.filetype === MimeTypes.TEXT) {
    const hasSegments = (note.segments?.length ?? 0) > 0;
    if (!hasSegments && note.content?.length > 0) {
      note.segments = segmentize(note.content);
      if (note.missedConversion) {
        note.documentNotices.push({
          message:
            "There was an error converting the documents to HTML. Instead we're showing you a plain text version.",
          level: CaseAlertType.WARNING
        });
      }
    }
  }

  return note;
};

/**
 * Takes an object with any number of properties of a CMxCommonApp.DecoratedNote and returns
 * a fully formed CMxCommonApp.DecoratedNote. Centralizes the construction of a CommonApp datastructure.
 * @param decoratedNote
 * @returns {CMxCommonApp.DecoratedNote}
 */
export const decoratedNoteFactory = (
  decoratedNote: Partial<CMxCommonApp.DecoratedNote>
): CMxCommonApp.DecoratedNote => {
  return {
    ...cloneDeep(EMPTY_NOTE),
    ...decoratedNote
  };
};

/** Primary note first, then by note id descending — use for nav tabs + document column order. */
export const compareDecoratedNotesForNav = (
  a: CMxCommonApp.DecoratedNote,
  b: CMxCommonApp.DecoratedNote
): number => {
  if (a.primary === true) return -1;
  if (b.primary === true) return 1;
  return (Number(b.id) || 0) - (Number(a.id) || 0);
};

export const firstAvailable = (
  groupBydDocs: Dictionary<CMxAPI.DocumentMetadata[]>,
  mimeTypes: string[]
) => {
  const mimeKey = mimeTypes.find(mimeType => {
    return groupBydDocs[mimeType] && groupBydDocs[mimeType].length > 0;
  });
  if (mimeKey) {
    return fetchLatestDoc(groupBydDocs[mimeKey]);
  }
};
